import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { PapiEditorText } from '@wix/da-papi-types';
import CustomBoxViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/CustomBox';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import Background from '@wix/da-shared-react/pkg/Background';
import { BackgroundInfo } from '@wix/da-shared-react/pkg/Background/types';
import { useMeasuredCookie } from '@wix/da-shared-react/pkg/Measured/useMeasuredCookie';
import { ZoneType } from '../../../../types/layout';
import { getMeasuredCookieType } from './helpers';

import s from './Custom.scss';

export interface Props extends BackgroundInfo {
  className?: string;
  title?: string;
  customtext: PapiEditorText;
  moduleId: number;
  moduleVersion: number;
  isOwner: boolean;
  isCurrentUserCoreMember: boolean;
  isNewCustombox?: boolean;
  width?: string;
  viewerKeySuffix?: string;
  zone: ZoneType;
  position?: number;
  noPadding?: boolean;
  showCoreUpsell?: boolean;
}
const Custom: React.FC<Props> = props => {
  const {
    isNewCustombox,
    title,
    imageDeviation,
    imageUrl,
    backgroundType,
    imageDisplayMode,
    imageTint,
    pattern,
    gradient,
    color,
    moduleId,
    moduleVersion,
    viewerKeySuffix,
    zone,
    position,
    noPadding,
  } = props;
  const backgroundProps = {
    title,
    imageDeviation,
    imageUrl,
    backgroundType,
    imageDisplayMode,
    imageTint,
    pattern,
    gradient,
    color,
  };

  // Force measured cache bust any time viewerKeySuffix updates
  const [measuredVersion, setMeasuredVersion] = useState(0);
  const [viewerKey, setViewerKey] = useState(viewerKeySuffix);
  useEffect(() => {
    if (viewerKeySuffix !== viewerKey) {
      setMeasuredVersion(measuredVersion + 1);
      setViewerKey(viewerKeySuffix);
    }
  }, [viewerKeySuffix, viewerKey, measuredVersion]);

  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const measuredCookieType = getMeasuredCookieType(zone, position);
  const measuredContainer = useMeasuredCookie(
    measuredCookieType,
    containerRef,
    {
      cookieDim: 'width',
      listenOnWindow: true,
      sentinelRef,
      version: measuredVersion,
    }
  );

  const width = measuredContainer?.width || 600;

  return (
    <>
      <div ref={sentinelRef} style={{ maxWidth: '100%' }} />
      <Background
        variant="custombox"
        className={classnames(
          'da-editor-custombox',
          s['root'],
          props.className,
          isNewCustombox && s['new-custombox']
        )}
        contentClassName={s['content']}
        {...backgroundProps}
        noPadding={noPadding}
        // TODO: custombox_templates_papi Pass in the template when the module data has it?
      >
        <div ref={containerRef} style={{ width: '100%' }}>
          <TextRenderer
            width={width}
            textContent={props.customtext}
            reactViewer={CustomBoxViewer}
            legacyTheme={'default'}
            viewerKey={`custombox-${moduleId}-${moduleVersion}-${width}`}
          />
        </div>
      </Background>
    </>
  );
};
Custom.displayName = 'Custom';

export default Custom;
